import { BehaviorSubject } from 'rxjs';
import {
  AttachmentDetailDto,
  AttachmentMergeDto,
  AttachmentReferenceDto,
  InspectionArchiveImagesDto,
  InspectionArchiveImagesMergeDto,
} from '@artemis-software/wr-api';
import { EventEmitter } from '@angular/core';

type OptionalKeys = 's3key' | 'presignedUrl' | 'inspectionId';

export type Attachment = AttachmentDetailDto & {
  deleted: boolean;
  buildingSectionId?: string | null;
};

export type PendingAttachment = {
  [K in OptionalKeys]?: Attachment[K];
} & {
  [K in Exclude<keyof Attachment, OptionalKeys>]: Attachment[K];
};

export interface AttachmentUploadJob {
  attachment: PendingAttachment;
}

export interface FormControlContext {
  pendingJobs$: BehaviorSubject<AttachmentUploadJob[]>;
  attachmentsChangeEventListener?: EventEmitter<Attachment[]>;
  newAttachmentsEventListener?: EventEmitter<Attachment[]>;
}

export function attachmentFromDetail(detail: AttachmentDetailDto): Attachment;
export function attachmentFromDetail(detail: AttachmentDetailDto | undefined): Attachment | undefined;
export function attachmentFromDetail(detail: AttachmentDetailDto | undefined): Attachment | undefined {
  return detail ? {
    ...detail,
    deleted: false,
  } : undefined;
}

export function attachmentsFromDetails(details: AttachmentDetailDto[]): Attachment[] {
  return details.map(d => attachmentFromDetail(d));
}

export function attachmentToMerge(attachment: Attachment): AttachmentMergeDto;
export function attachmentToMerge(attachment: Attachment | undefined | null): AttachmentMergeDto | undefined;
export function attachmentToMerge(attachment: Attachment | undefined | null): AttachmentMergeDto | undefined {
  return attachment ? {
    s3Key: attachment.s3key,
    rotationDegrees: normalizeRotationDegrees(attachment.rotationDegrees),
    tags: attachment.tags,
    comment: attachment.comment,
    deleted: attachment.deleted,
  } : undefined;
}

export function attachmentsFromArchiveImages(archiveImages: InspectionArchiveImagesDto[]): Attachment[] {
  return archiveImages.flatMap(group =>
    group.attachments.map(detail => ({
      ...attachmentFromDetail(detail),
      buildingSectionId: group.buildingSectionId ?? null,
    })),
  );
}

export function archiveImagesFromAttachments(attachments: Attachment[]): InspectionArchiveImagesMergeDto[] {
  const groups = new Map<string | null, Attachment[]>();
  for (const attachment of attachments) {
    const key = attachment.buildingSectionId ?? null;
    const bucket = groups.get(key);
    if (bucket) {
      bucket.push(attachment);
    } else {
      groups.set(key, [attachment]);
    }
  }
  return Array.from(groups.entries()).map(([buildingSectionId, groupAttachments]) => ({
    buildingSectionId: buildingSectionId ?? undefined,
    attachments: groupAttachments.map(a => attachmentToMerge(a)),
  }));
}

export const normalizeRotationDegrees = (degrees: number): number => {
  const normalizedDegrees = degrees % 360;
  if (normalizedDegrees >= 0) {
    return normalizedDegrees;
  }
  return normalizedDegrees + 360;
};

export function attachmentDetailToAttachmentReferenceDto(detail: Attachment | undefined): AttachmentReferenceDto {
  return {
    value: attachmentToMerge(detail),
  };
}
